import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Box } from '@mui/material';

import { RootState } from './store/index';
import { Layout } from './components/Layout';
import { LoginPage } from './pages/LoginPage';
import { SsoPage } from './pages/SsoPage';
import { DashboardPage } from './pages/DashboardPage';
import { RadarClientePage } from './pages/RadarClientePage';
import { PreferencesPage } from './pages/PreferencesPage';
import { LogsPage } from './pages/LogsPage';
import { SettingsPage } from './pages/SettingsPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { loadSsoNav, safeExternalUrl } from './utils/ssoNav';

// Padrão quando não sabemos a origem: o sistema Greenplat (a Legislação é
// acessada via SSO de lá, não pela tela de login própria).
const LOGGED_OUT_HOME =
  process.env.REACT_APP_LOGGED_OUT_URL || 'https://sistema.plataformaverde.com.br';

// Para onde mandar o visitante DESLOGADO: de volta para ONDE ELE LOGOU (a
// origem guardada no SSO nav — PV ou H2O); se não houver origem, cai no padrão
// (PV). O /login continua acessível explicitamente (ex.: admin).
function loggedOutTarget(): string {
  const nav = loadSsoNav();
  return safeExternalUrl(nav.home_url) || safeExternalUrl(nav.pv_url) || LOGGED_OUT_HOME;
}

function ExternalRedirect({ to }: { to: string }) {
  React.useEffect(() => {
    window.location.replace(to);
  }, [to]);
  return null;
}

function App() {
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const location = useLocation();

  // /sso é SEMPRE processado sozinho (sem Layout), mesmo que exista uma sessão
  // antiga no localStorage. Se o Layout renderizasse aqui, ele dispararia
  // requisições com o token velho e um 401 chutaria o usuário para /login antes
  // de o SSO concluir — era exatamente o bug do "só no 1º acesso".
  if (location.pathname === '/sso') {
    return (
      <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <SsoPage />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="*" element={<ExternalRedirect to={loggedOutTarget()} />} />
        </Routes>
      </Box>
    );
  }

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/changes" element={<Navigate to="/dashboard" replace />} />
        <Route path="/timeline" element={<Navigate to="/dashboard" replace />} />
        <Route path="/legislation" element={<Navigate to="/dashboard" replace />} />
        <Route path="/radar" element={<RadarClientePage />} />
        <Route path="/agencias" element={<Navigate to="/dashboard" replace />} />
        <Route path="/sources" element={<Navigate to="/dashboard" replace />} />
        <Route path="/preferences" element={<PreferencesPage />} />
        <Route path="/logs" element={<LogsPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Layout>
  );
}

export default App;
